import { Badge, Box, Flex, Text } from "@mantine/core";
import { MinusIcon, TrendDownIcon, TrendUpIcon } from "@phosphor-icons/react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import pageClasses from "../../pages/customer-billing/BillingDetails.module.css";

interface PaymentTrendItem {
  month: string;
  paidCount: number;
  avgDays: number;
  onTimeCount: number;
  lateCount: number;
}

interface Props {
  paymentTrend: PaymentTrendItem[] | null;
  latePaymentTrend: string | null;
}

export function PaymentBehaviorChart({ paymentTrend, latePaymentTrend }: Props) {
  const { t } = useTranslation("billing");

  if (!paymentTrend || paymentTrend.length === 0) return null;

  const TREND_CONFIG: Record<string, { color: string; icon: typeof TrendUpIcon; label: string }> = {
    IMPROVING: { color: "green", icon: TrendDownIcon, label: t("ar.trendImproving") },
    STABLE: { color: "gray", icon: MinusIcon, label: t("ar.trendStable") },
    WORSENING: { color: "red", icon: TrendUpIcon, label: t("ar.trendWorsening") },
  };

  const trend = latePaymentTrend ? TREND_CONFIG[latePaymentTrend] : null;
  const TrendIcon = trend?.icon;

  const options: Highcharts.Options = {
    chart: {
      height: 250,
      backgroundColor: "transparent",
    },
    title: { text: "" },
    xAxis: {
      categories: paymentTrend.map((r) => r.month),
      crosshair: true,
    },
    yAxis: [
      {
        title: { text: t("ar.yAxisDays") },
        labels: { style: { color: "var(--mantine-color-brand-6)" } },
      },
      {
        title: { text: t("ar.yAxisCount") },
        opposite: true,
        labels: { style: { color: "var(--mantine-color-green-6)" } },
      },
    ],
    tooltip: { shared: true },
    legend: { align: "center", verticalAlign: "bottom" },
    series: [
      {
        name: t("ar.seriesAvgDays"),
        type: "line",
        data: paymentTrend.map((r) => r.avgDays),
        color: "var(--mantine-color-brand-6)",
        yAxis: 0,
        tooltip: { valueSuffix: t("ar.tooltipDaySuffix") },
      },
      {
        name: t("ar.seriesOnTime"),
        type: "column",
        data: paymentTrend.map((r) => r.onTimeCount),
        color: "var(--mantine-color-green-6)",
        yAxis: 1,
      },
      {
        name: t("ar.seriesLate"),
        type: "column",
        data: paymentTrend.map((r) => r.lateCount),
        color: "var(--mantine-color-red-6)",
        yAxis: 1,
      },
    ],
    accessibility: { enabled: false },
    credits: { enabled: false },
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb="sm">
        <Text fw={600} fz={15} c="gray.9">
          {t("ar.paymentBehavior")}
        </Text>
        {trend && TrendIcon && (
          <Badge color={trend.color} variant="light">
            <Flex align="center" gap={4}>
              <TrendIcon size="0.9rem" />
              <Text size="xs">{trend.label}</Text>
            </Flex>
          </Badge>
        )}
      </Flex>
      <Box className={pageClasses.card} style={{ padding: 12, boxShadow: "none" }}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </Box>
    </Box>
  );
}
